import { useState, useEffect, useCallback } from 'react'
import { appsyncQuery } from '../api/appsync-client.js'

/**
 * useInSession — fetch and mutate the typed in-session rows for a session.
 *
 * Returns all six entity collections (talkingPoints, decisions, notes,
 * pullEdges) and mutation helpers for each. Mirrors the shape of usePrep.js.
 *
 * Phase F co-edit: The onSessionRowChanged subscription is deferred — AppSync
 * subscriptions require a union return type to fan out across entity types,
 * which is Phase F follow-up work. The hook polls via reload() instead.
 * // Phase F follow-up: wire onSessionRowChanged subscription for live co-edit.
 *
 * Phase D/F: sessionId is required. The caller (InSessionPage) also passes
 * the `me` object so notes/decisions know who is the report vs manager.
 */

const GET_SESSION_ROWS = `
  query GetSessionRows($sessionId: ID!) {
    getSessionRows(sessionId: $sessionId) {
      talkingPoints {
        talkingPointId sessionId text order status
        sourceBulletId sourcePrepCardKind addedBy createdAt updatedAt
      }
      decisions {
        decisionId sessionId text capturedBy agreedBy createdAt updatedAt
      }
      notes {
        slot sessionId text updatedAt
      }
      pullEdges {
        pullId sessionId fromId toId createdAt
      }
    }
  }
`

const ADD_TALKING_POINT = `
  mutation AddTalkingPoint($sessionId: ID!, $input: AddTalkingPointInput!) {
    addTalkingPoint(sessionId: $sessionId, input: $input) {
      talkingPointId sessionId text order status
      sourceBulletId sourcePrepCardKind addedBy createdAt updatedAt
    }
  }
`

const UPDATE_TALKING_POINT = `
  mutation UpdateTalkingPoint($sessionId: ID!, $talkingPointId: ID!, $input: UpdateTalkingPointInput!) {
    updateTalkingPoint(sessionId: $sessionId, talkingPointId: $talkingPointId, input: $input) {
      talkingPointId sessionId text order status
      sourceBulletId sourcePrepCardKind addedBy createdAt updatedAt
    }
  }
`

const REMOVE_TALKING_POINT = `
  mutation RemoveTalkingPoint($sessionId: ID!, $talkingPointId: ID!) {
    removeTalkingPoint(sessionId: $sessionId, talkingPointId: $talkingPointId)
  }
`

const ADD_DECISION = `
  mutation AddDecision($sessionId: ID!, $input: AddDecisionInput!) {
    addDecision(sessionId: $sessionId, input: $input) {
      decisionId sessionId text capturedBy agreedBy createdAt updatedAt
    }
  }
`

const UPDATE_DECISION = `
  mutation UpdateDecision($sessionId: ID!, $decisionId: ID!, $input: UpdateDecisionInput!) {
    updateDecision(sessionId: $sessionId, decisionId: $decisionId, input: $input) {
      decisionId sessionId text capturedBy agreedBy createdAt updatedAt
    }
  }
`

const REMOVE_DECISION = `
  mutation RemoveDecision($sessionId: ID!, $decisionId: ID!) {
    removeDecision(sessionId: $sessionId, decisionId: $decisionId)
  }
`

const UPDATE_SESSION_NOTE = `
  mutation UpdateSessionNote($sessionId: ID!, $slot: String!, $input: UpdateNoteInput!) {
    updateSessionNote(sessionId: $sessionId, slot: $slot, input: $input) {
      slot sessionId text updatedAt
    }
  }
`

const PULL_FROM_PREP = `
  mutation PullFromPrep($sessionId: ID!, $input: PullFromPrepInput!) {
    pullFromPrep(sessionId: $sessionId, input: $input) {
      talkingPoint {
        talkingPointId sessionId text order status
        sourceBulletId sourcePrepCardKind addedBy createdAt updatedAt
      }
      pullEdge {
        pullId sessionId fromId toId createdAt
      }
    }
  }
`

export function useInSession(sessionId, { enabled = true } = {}) {
  const [rows, setRows] = useState(null)
  const [loading, setLoading] = useState(!!(sessionId && enabled))
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!sessionId) return
    setLoading(true)
    setError(null)
    try {
      const data = await appsyncQuery(GET_SESSION_ROWS, { sessionId })
      setRows(data.getSessionRows)
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [sessionId])

  useEffect(() => {
    if (enabled && sessionId) load()
  }, [load, enabled, sessionId])

  // ── Helpers ────────────────────────────────────────────────────────────────

  const getNoteText = useCallback(
    (slot) => rows?.notes?.find((n) => n.slot === slot)?.text ?? '',
    [rows],
  )

  // ── TalkingPoint mutations ─────────────────────────────────────────────────

  const addTalkingPoint = useCallback(async (input) => {
    const data = await appsyncQuery(ADD_TALKING_POINT, { sessionId, input })
    const tp = data.addTalkingPoint
    setRows((prev) => prev ? { ...prev, talkingPoints: [...prev.talkingPoints, tp] } : prev)
    return tp
  }, [sessionId])

  const updateTalkingPoint = useCallback(async (talkingPointId, input) => {
    const data = await appsyncQuery(UPDATE_TALKING_POINT, { sessionId, talkingPointId, input })
    const updated = data.updateTalkingPoint
    setRows((prev) =>
      prev
        ? {
            ...prev,
            talkingPoints: prev.talkingPoints.map((tp) =>
              tp.talkingPointId === talkingPointId ? { ...tp, ...updated } : tp,
            ),
          }
        : prev,
    )
    return updated
  }, [sessionId])

  const removeTalkingPoint = useCallback(async (talkingPointId) => {
    await appsyncQuery(REMOVE_TALKING_POINT, { sessionId, talkingPointId })
    setRows((prev) =>
      prev
        ? {
            ...prev,
            talkingPoints: prev.talkingPoints.filter(
              (tp) => tp.talkingPointId !== talkingPointId,
            ),
          }
        : prev,
    )
  }, [sessionId])

  // ── Decision mutations ────────────────────────────────────────────────────

  const addDecision = useCallback(async (input) => {
    const data = await appsyncQuery(ADD_DECISION, { sessionId, input })
    const dec = data.addDecision
    setRows((prev) => prev ? { ...prev, decisions: [...prev.decisions, dec] } : prev)
    return dec
  }, [sessionId])

  const updateDecision = useCallback(async (decisionId, input) => {
    const data = await appsyncQuery(UPDATE_DECISION, { sessionId, decisionId, input })
    const updated = data.updateDecision
    setRows((prev) =>
      prev
        ? {
            ...prev,
            decisions: prev.decisions.map((d) =>
              d.decisionId === decisionId ? { ...d, ...updated } : d,
            ),
          }
        : prev,
    )
    return updated
  }, [sessionId])

  const removeDecision = useCallback(async (decisionId) => {
    await appsyncQuery(REMOVE_DECISION, { sessionId, decisionId })
    setRows((prev) =>
      prev
        ? { ...prev, decisions: prev.decisions.filter((d) => d.decisionId !== decisionId) }
        : prev,
    )
  }, [sessionId])

  // ── Note mutations ─────────────────────────────────────────────────────────

  const updateNote = useCallback(async (slot, text) => {
    const data = await appsyncQuery(UPDATE_SESSION_NOTE, { sessionId, slot, input: { text } })
    const updated = data.updateSessionNote
    setRows((prev) => {
      if (!prev) return prev
      const notes = prev.notes.some((n) => n.slot === slot)
        ? prev.notes.map((n) => (n.slot === slot ? { ...n, ...updated } : n))
        : [...prev.notes, updated]
      return { ...prev, notes }
    })
    return updated
  }, [sessionId])

  // ── Pull mechanic ─────────────────────────────────────────────────────────

  const pullFromPrep = useCallback(async (bulletId, text) => {
    const input = { bulletId, ...(text != null && { text }) }
    const data = await appsyncQuery(PULL_FROM_PREP, { sessionId, input })
    const { talkingPoint, pullEdge } = data.pullFromPrep
    setRows((prev) =>
      prev
        ? {
            ...prev,
            talkingPoints: [...prev.talkingPoints, talkingPoint],
            pullEdges: [...prev.pullEdges, pullEdge],
          }
        : prev,
    )
    return { talkingPoint, pullEdge }
  }, [sessionId])

  return {
    rows,
    loading,
    error,
    reload: load,
    getNoteText,
    addTalkingPoint,
    updateTalkingPoint,
    removeTalkingPoint,
    addDecision,
    updateDecision,
    removeDecision,
    updateNote,
    pullFromPrep,
  }
}
